import { useState, useEffect, useCallback, useRef } from 'react';
import Header from './components/Header';
import Sidebar from './components/Sidebar';
import CodeEditor from './components/CodeEditor';
import Preview from './components/Preview';
import StatusMessage from './components/StatusMessage';
import StyleControls from './components/StyleControls';
import { SAMPLE_DIAGRAMS } from './data/sampleDiagrams';
import { generateId } from './utils/dateUtils';
import { detectDiagramType } from './utils/diagramUtils';
import {
  getDiagramSVG,
  initializeMermaid,
  getThemeVariablesFromCode,
  applyThemeVariablesToCode,
  removeThemeVariablesFromCode
} from './utils/mermaidUtils';
import { copyToClipboard, downloadAsFile } from './utils/fileUtils';

const DEFAULT_STYLE_VALUES = {
  primaryColor: '#2190D1',
  primaryTextColor: '#FFFFFF',
  lineColor: '#157F52',
  background: '#F4F4F4',
  fontFamily: '',
  fontSize: ''
};

function App() {
  // Detect if mobile on initial load
  const isMobileDevice = () => window.innerWidth <= 768;
  
  // Application state
  const [theme, setTheme] = useState('light');
  const [code, setCode] = useState('');
  const [savedDiagrams, setSavedDiagrams] = useState([]);
  const [sidebarVisible, setSidebarVisible] = useState(!isMobileDevice()); // Hidden by default on mobile
  const [activeTab, setActiveTab] = useState('samples');
  const [statusMessage, setStatusMessage] = useState('');
  const [statusType, setStatusType] = useState('success');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [styleValues, setStyleValues] = useState({ ...DEFAULT_STYLE_VALUES });
  const [zoom, setZoom] = useState(1);
  const [previewKey, setPreviewKey] = useState(0);
  const previewRef = useRef(null);

  // Initialize app with sample diagram
  useEffect(() => {
    if (SAMPLE_DIAGRAMS.length > 0) {
      setCode(SAMPLE_DIAGRAMS[0].code);
    }

    // Apply theme to document
    document.documentElement.setAttribute('data-theme', theme);
    
    // Initialize mermaid
    initializeMermaid(theme);
  }, []);

  // Update theme
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    initializeMermaid(theme);
  }, [theme]);

  // Manage body class for sidebar backdrop on mobile
  useEffect(() => {
    if (window.innerWidth <= 768) {
      if (sidebarVisible) {
        document.body.classList.add('sidebar-open');
      } else {
        document.body.classList.remove('sidebar-open');
      }
    }
    
    return () => {
      document.body.classList.remove('sidebar-open');
    };
  }, [sidebarVisible]);

  // Listen for custom close sidebar event
  useEffect(() => {
    const handleCloseSidebar = () => {
      setSidebarVisible(false);
    };
    
    window.addEventListener('closeSidebar', handleCloseSidebar);
    
    return () => {
      window.removeEventListener('closeSidebar', handleCloseSidebar);
    };
  }, []);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Handle Escape key to exit fullscreen OR close sidebar on mobile
      if (e.key === 'Escape') {
        if (isFullscreen) {
          setIsFullscreen(false);
          return;
        }
        if (window.innerWidth <= 768 && sidebarVisible) {
          setSidebarVisible(false);
          return;
        }
      }

      // Handle F11 for fullscreen toggle
      if (e.key === 'F11') {
        e.preventDefault();
        setIsFullscreen(!isFullscreen);
        return;
      }

      if (e.ctrlKey || e.metaKey) {
        switch (e.key) {
          case 's':
            e.preventDefault();
            handleSave();
            break;
          case 'n':
            e.preventDefault();
            handleClear();
            break;
          case 'b':
            e.preventDefault();
            setSidebarVisible(!sidebarVisible);
            break;
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [sidebarVisible, code, isFullscreen]);

  // Show status message
  const showStatus = useCallback((message, type = 'success') => {
    setStatusMessage(message);
    setStatusType(type);
  }, []);

  // Hide status message
  const hideStatus = useCallback(() => {
    setStatusMessage('');
  }, []);

  // Handle theme change
  const handleThemeChange = useCallback((newTheme) => {
    setTheme(newTheme);
  }, []);

  // Handle save diagram
  const handleSave = useCallback(() => {
    if (!code.trim()) {
      showStatus('No code to save', 'error');
      return;
    }

    const diagramType = detectDiagramType(code);
    const timestamp = Date.now();
    
    // Generate a simple name based on type and timestamp
    const diagramName = `${diagramType.charAt(0).toUpperCase() + diagramType.slice(1)} - ${new Date().toLocaleDateString()}`;
    
    const newDiagram = {
      id: generateId(),
      name: diagramName,
      type: diagramType,
      code: code,
      createdAt: timestamp,
      updatedAt: timestamp
    };

    setSavedDiagrams(prev => [newDiagram, ...prev]);
    setActiveTab('history');
    showStatus('Diagram saved successfully!');
  }, [code, showStatus]);

  // Handle clear editor
  const handleClear = useCallback(() => {
    setCode('');
  }, []);

  // Handle export SVG
  const handleExportSVG = useCallback(() => {
    const svg = getDiagramSVG('mermaid-preview');
    if (svg) {
      const filename = `mermaid-diagram-${Date.now()}.svg`;
      downloadAsFile(svg, filename, 'image/svg+xml');
      showStatus('SVG exported successfully!');
    } else {
      showStatus('No diagram to export', 'error');
    }
  }, [showStatus]);

  // Handle copy code
  const handleCopy = useCallback(async () => {
    if (!code.trim()) {
      showStatus('No code to copy', 'error');
      return;
    }

    try {
      const success = await copyToClipboard(code);
      if (success) {
        showStatus('Code copied to clipboard!');
      } else {
        showStatus('Failed to copy code', 'error');
      }
    } catch (err) {
      showStatus('Failed to copy code', 'error');
    }
  }, [code, showStatus]);

  const handleStyleValueChange = useCallback((key, value) => {
    setStyleValues(prev => ({
      ...prev,
      [key]: value
    }));
  }, []);

  const handleApplyStyles = useCallback(() => {
    if (!code.trim()) {
      showStatus('No diagram to style', 'error');
      return;
    }

    const updatedCode = applyThemeVariablesToCode(code, styleValues);
    setCode(updatedCode);
    showStatus('Diagram styling applied!');
  }, [code, styleValues, showStatus]);

  const handleResetStyles = useCallback(() => {
    if (!code.trim()) {
      setStyleValues({ ...DEFAULT_STYLE_VALUES });
      return;
    }

    const updatedCode = removeThemeVariablesFromCode(code);
    setCode(updatedCode);
    setStyleValues({ ...DEFAULT_STYLE_VALUES });
    showStatus('Diagram styling reset');
  }, [code, showStatus]);

  // Handle load diagram
  const handleLoadDiagram = useCallback((diagramCode) => {
    setCode(diagramCode);
    showStatus('Diagram loaded!');
    // Auto-close sidebar on mobile after loading
    if (window.innerWidth <= 768) {
      setSidebarVisible(false);
    }
  }, [showStatus]);

  // Handle delete diagram
  const handleDeleteDiagram = useCallback((diagramId) => {
    setSavedDiagrams(prev => prev.filter(d => d.id !== diagramId));
    showStatus('Diagram deleted');
  }, [showStatus]);

  // Toggle sidebar
  const handleToggleSidebar = useCallback(() => {
    setSidebarVisible(!sidebarVisible);
  }, [sidebarVisible]);

  // Close sidebar when clicking outside on mobile
  useEffect(() => {
    const handleClickOutside = (e) => {
      // Only on mobile/tablet screens
      if (window.innerWidth <= 768 && sidebarVisible) {
        const sidebar = document.querySelector('.sidebar');
        const headerToggle = document.querySelector('.header__logo button');
        
        // Close if clicking outside sidebar and not on the toggle button
        if (sidebar && !sidebar.contains(e.target) && !headerToggle?.contains(e.target)) {
          setSidebarVisible(false);
        }
      }
    };

    // Add event listener with a small delay to avoid immediate closure
    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }, 100);
    
    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [sidebarVisible]);

  // Toggle fullscreen
  const handleToggleFullscreen = useCallback(() => {
    setIsFullscreen(!isFullscreen);
  }, [isFullscreen]);

  // Zoom handlers
  const handleZoomIn = useCallback(() => {
    if (previewRef.current?.handleZoomIn) {
      previewRef.current.handleZoomIn();
    } else {
      setZoom(prev => Math.min(prev + 0.1, 3));
      setPreviewKey(prev => prev + 1);
    }
  }, []);

  const handleZoomOut = useCallback(() => {
    if (previewRef.current?.handleZoomOut) {
      previewRef.current.handleZoomOut();
    } else {
      setZoom(prev => Math.max(prev - 0.1, 0.3));
      setPreviewKey(prev => prev + 1);
    }
  }, []);

  const handleZoomFit = useCallback(() => {
    if (previewRef.current?.handleZoomFit) {
      previewRef.current.handleZoomFit();
    } else {
      setZoom(1);
      setPreviewKey(prev => prev + 1);
    }
  }, []);

  const handleZoomActual = useCallback(() => {
    if (previewRef.current?.handleZoomActual) {
      previewRef.current.handleZoomActual();
    } else {
      setZoom(1);
      setPreviewKey(prev => prev + 1);
    }
  }, []);

  useEffect(() => {
    if (!code.trim()) {
      setStyleValues({ ...DEFAULT_STYLE_VALUES });
      return;
    }

    const extracted = getThemeVariablesFromCode(code);
    if (!extracted || Object.keys(extracted).length === 0) {
      setStyleValues({ ...DEFAULT_STYLE_VALUES });
      return;
    }

    const normalized = Object.entries(extracted).reduce((acc, [key, value]) => {
      if (typeof value === 'string') {
        const shouldUppercase = key.toLowerCase().includes('color') || key === 'background';
        acc[key] = shouldUppercase ? value.toUpperCase() : value;
      }
      return acc;
    }, {});

    setStyleValues({
      ...DEFAULT_STYLE_VALUES,
      ...normalized
    });
  }, [code]);

  return (
    <div className={`app ${isFullscreen ? 'fullscreen-mode' : ''}`}>
      {/* Status Message */}
      <StatusMessage
        message={statusMessage}
        type={statusType}
        onClose={hideStatus}
      />

      {/* Header */}
      <Header
        theme={theme}
        onThemeChange={handleThemeChange}
        onToggleSidebar={handleToggleSidebar}
        sidebarVisible={sidebarVisible}
        onSave={handleSave}
        onClear={handleClear}
        onExportSVG={handleExportSVG}
        onCopy={handleCopy}
        hasContent={Boolean(code.trim())}
        onZoomIn={handleZoomIn}
        onZoomOut={handleZoomOut}
        onZoomFit={handleZoomFit}
        onZoomActual={handleZoomActual}
        zoomLevel={zoom}
        onToggleFullscreen={handleToggleFullscreen}
        isFullscreen={isFullscreen}
      />

      {/* Main Layout */}
      <main className="main-layout">
        {/* Sidebar */}
        <Sidebar
          visible={sidebarVisible}
          activeTab={activeTab}
          onTabChange={setActiveTab}
          savedDiagrams={savedDiagrams}
          onLoadDiagram={handleLoadDiagram}
          onDeleteDiagram={handleDeleteDiagram}
        />

        {/* Editor Container */}
        <div className="editor-container">
          {/* Editor Header */}
          <div className="editor-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <h3 style={{ margin: 0, fontSize: 'var(--font-size-md)' }}>
              Mermaid Code Editor
            </h3>
            <span style={{ color: 'var(--color-text-secondary)', fontSize: 'var(--font-size-sm)' }}>
              Preview
            </span>
          </div>

          {/* Editor Content */}
          <div className="editor-content">
            {/* Code Editor Pane */}
            <div className="editor-pane">
              <StyleControls
                values={styleValues}
                onValueChange={handleStyleValueChange}
                onApply={handleApplyStyles}
                onReset={handleResetStyles}
                isApplyDisabled={!code.trim()}
              />
              <CodeEditor value={code} onChange={setCode} />
            </div>

            {/* Preview Pane */}
            <div className="preview-pane">
              <Preview 
                ref={previewRef}
                code={code} 
                theme={theme} 
                isFullscreen={isFullscreen}
                zoom={zoom}
                previewKey={previewKey}
                onCodeChange={setCode}
                onZoomChange={setZoom}
              />
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

export default App;
